"use client"

import { useEffect, useMemo, useState } from "react"
import axios from "axios"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import PrintIcon from "@mui/icons-material/Print"
import {
  getPrinterNumberCookie,
  setPrinterNumberCookie,
  clearPrinterNumberCookie,
  getTenantKey,
} from "../../context/tickeadora/printerCookieUtils.js"

const MODE = process.env.REACT_APP_MODE
const serverURL =
  MODE === "production"
    ? process.env.REACT_APP_URL_PROD || "http://localhost:5000"
    : process.env.REACT_APP_URL_DEV || "http://localhost:5000"

/**
 * Modal único para ver/configurar el número de impresora del browser (cookie)
 * y guiar al operador con sucursales (nombre + id).
 */
export default function ModalConfigurarImpresora({
  open,
  onClose,
  mode = "configure",
  suggestedPrinterNumber = "",
  idUbicacion = null,
  ubicaciones: ubicacionesProp = null,
  onSaved,
}) {
  const isRequired = mode === "required"
  const tenant = getTenantKey()

  const [inputValue, setInputValue] = useState("")
  const [cookieValue, setCookieValue] = useState(null)
  const [ubicaciones, setUbicaciones] = useState([])
  const [loadingUbicaciones, setLoadingUbicaciones] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!open) return

    const current = getPrinterNumberCookie(tenant)
    setCookieValue(current)
    setError("")

    const suggested =
      current ||
      (suggestedPrinterNumber != null && suggestedPrinterNumber !== ""
        ? String(suggestedPrinterNumber)
        : idUbicacion != null
          ? String(idUbicacion)
          : "")
    setInputValue(suggested)

    let cancelado = false
    ;(async () => {
      if (Array.isArray(ubicacionesProp) && ubicacionesProp.length > 0) {
        setUbicaciones(ubicacionesProp)
      } else {
        setLoadingUbicaciones(true)
        try {
          const response = await axios.get(`${serverURL}/adminAPI/getUbicacion`, {
            withCredentials: true,
          })
          if (!cancelado) {
            setUbicaciones(Array.isArray(response.data) ? response.data : [])
          }
        } catch (err) {
          if (!cancelado) setUbicaciones([])
        } finally {
          if (!cancelado) setLoadingUbicaciones(false)
        }
      }
    })()

    return () => {
      cancelado = true
    }
  }, [open, tenant, suggestedPrinterNumber, idUbicacion, ubicacionesProp])

  const sucursalActual = useMemo(() => {
    if (idUbicacion == null) return null
    return ubicaciones.find((u) => String(u.id) === String(idUbicacion)) || null
  }, [ubicaciones, idUbicacion])

  const handleGuardar = () => {
    const value = String(inputValue || "").trim()
    if (!value) {
      setError("Ingresá un número de impresora")
      return
    }
    setPrinterNumberCookie(value, tenant)
    setCookieValue(value)
    onSaved?.({ printerNumber: value, cancelled: false })
  }

  const handleLimpiar = () => {
    clearPrinterNumberCookie(tenant)
    setCookieValue(null)
    setInputValue("")
    setError("")
  }

  const handleCancelar = () => {
    onClose?.({ cancelled: true })
  }

  return (
    <Dialog
      open={open}
      onClose={isRequired ? undefined : handleCancelar}
      maxWidth="sm"
      fullWidth
      // Por encima del Modal de factura post-venta (zIndex.modal = 1300)
      sx={{ zIndex: (theme) => theme.zIndex.modal + 200 }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
        <PrintIcon color="primary" />
        Configurar impresora de tickets
        <IconButton
          aria-label="cerrar"
          onClick={handleCancelar}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isRequired && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No hay impresora configurada en este navegador. Ingresá el número para poder imprimir.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          El número debe coincidir con el configurado en el agente local de esta PC
          (menú Configurar del agente). Suele ser el mismo id que la sucursal.
        </Typography>

        {sucursalActual && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Sucursal de la venta: <strong>{sucursalActual.descripcion || sucursalActual.nombre || "—"}</strong>
            {" "}(id {sucursalActual.id})
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 2 }}>
          <TextField
            label="Número de impresora"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              setError("")
            }}
            fullWidth
            size="small"
            autoFocus
            error={Boolean(error)}
            helperText={error || (cookieValue ? `Cookie actual: ${cookieValue}` : "Sin configurar en cookie")}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleGuardar()
            }}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Sucursales (guía operativa)
        </Typography>
        {loadingUbicaciones ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : ubicaciones.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No se pudieron cargar las sucursales.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 180, overflow: "auto", mb: 1 }}>
            {ubicaciones
              .filter((u) => !u.eliminado)
              .map((u) => (
                <ListItem
                  key={u.id}
                  dense
                  secondaryAction={
                    <Chip
                      size="small"
                      label={`id ${u.id}`}
                      color={String(u.id) === String(idUbicacion) ? "primary" : "default"}
                      variant={String(u.id) === String(idUbicacion) ? "filled" : "outlined"}
                    />
                  }
                >
                  <ListItemText
                    primary={u.descripcion || u.nombre || `Sucursal ${u.id}`}
                    secondary={String(u.id) === String(idUbicacion) ? "Sucursal de esta venta" : null}
                  />
                </ListItem>
              ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: "space-between" }}>
        <Button color="inherit" onClick={handleLimpiar} disabled={!cookieValue && !inputValue}>
          Limpiar cookie
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={handleCancelar}>{isRequired ? "Cancelar" : "Cerrar"}</Button>
          <Button variant="contained" onClick={handleGuardar}>
            Guardar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
